import { api } from "./api";

const BASE = "/api/v1/integrations/cropwise";

/**
 * Cropwise Operations integration client.
 *
 * Two differences from johnDeereService worth knowing:
 *
 *  - There is no OAuth redirect. The user generates a token in Cropwise and
 *    pastes it, so `connect` is a POST with the token rather than a navigation.
 *  - Every route is behind the `cropwise` feature config and answers **404** to
 *    anyone not on the allowlist. Callers must therefore treat a 404 as "not
 *    enabled for me", not as an error worth showing.
 */

export interface CropwiseStatus {
  connected: boolean;
  accountId: string | null;
  /** Where the user generates the token — rendered as a link in the UI. */
  tokenPageUrl: string;
}

export interface CropwiseField {
  id: string;
  name: string;
  area?: number | null;
  /** False when Cropwise has no shape for the field — importing gives a field with no boundary. */
  hasGeometry: boolean;
}

export interface CropwiseImportResult {
  imported: Array<{ id: string; name: string; externalId: string; hasGeometry: boolean }>;
  errors?: Array<{ fieldId: string; error: string }>;
}

export interface CropwiseYieldSyncResult {
  status:
    | "synced"
    | "skipped-unchanged"
    | "no-data"
    | "not-linked"
    | "not-connected"
    | "unsupported"
    | "failed";
  ref?: string | null;
  unit?: string;
  pointCount?: number;
  effectiveMassKg?: number;
  error?: string;
  /** Cropwise's verbatim rejection body when status is "unsupported". */
  details?: string;
}

export interface CropwiseSyncSettings {
  /** When true, yields sync to Cropwise automatically. Opt-in: OFF after a new connection. */
  autoSyncYields: boolean;
  /** WeightUnit value, e.g. "t", "bag". Account-scoped. */
  weight: string;
  /** AreaUnit value, e.g. "ha", "ac". Account-scoped. */
  area: string;
  poundsPerBag?: number | null;
  poundsPerBushel?: number | null;
}

/**
 * `silent` because a 404 here is a normal answer, not a fault: the whole router
 * is behind the `cropwise` feature config and answers 404 to anyone outside it.
 * Without this, probing the status would pop "Not found" at every ungated user.
 * Callers treat any rejection as "not available" and render nothing.
 */
export const getCropwiseStatus = (): Promise<CropwiseStatus> =>
  api.get<CropwiseStatus>(`${BASE}/status`, undefined, { silent: true });

/** Validate and store a pasted API token. Rejects with a plain message on a bad token. */
export const connectCropwise = (apiToken: string): Promise<{ connected: boolean; accountId: string }> =>
  api.post<{ connected: boolean; accountId: string }>(`${BASE}/connect`, { apiToken });

export const disconnectCropwise = (): Promise<{ connected: boolean }> =>
  api.post<{ connected: boolean }>(`${BASE}/disconnect`, {});

export const getCropwiseFields = (): Promise<{ fields: CropwiseField[] }> =>
  api.get<{ fields: CropwiseField[] }>(`${BASE}/fields`);

export const importCropwiseFields = (body: {
  fieldIds: string[];
  farmId?: string;
}): Promise<CropwiseImportResult> => api.post<CropwiseImportResult>(`${BASE}/import`, body);

export const syncYieldToCropwise = (body: {
  fieldId: string;
  seasonId: string;
  force?: boolean;
  weight?: string;
  area?: string;
}): Promise<CropwiseYieldSyncResult> =>
  api.post<CropwiseYieldSyncResult>(`${BASE}/yield/sync`, body);

export const getCropwiseSyncSettings = (): Promise<CropwiseSyncSettings> =>
  api.get<CropwiseSyncSettings>(`${BASE}/settings`);

export const putCropwiseSyncSettings = (
  body: CropwiseSyncSettings
): Promise<CropwiseSyncSettings> => api.put<CropwiseSyncSettings>(`${BASE}/settings`, body);
